import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { identifyUser, resetUser } from '../services/analytics';
import * as adminSession from '../api/adminSession';
import {
  identifySentryUser,
  resetSentryUser,
  setSentryTag,
  addBreadcrumb,
} from '../services/sentry';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAdminSession: boolean;
  adminToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  setAdminSession: (accessToken: string, refreshToken: string, email: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdminSession, setIsAdminSession] = useState(() => adminSession.isAdminSession());
  const [adminToken, setAdminToken] = useState<string | null>(() => adminSession.getAdminAccessToken());

  useEffect(() => {
    // Admin sessions behave differently enough — different tokens, different
    // failure modes — that issues from them need to be separable.
    setSentryTag('session_kind', isAdminSession ? 'admin' : 'firebase');

    // If this is an admin session, don't wait for Firebase auth
    if (isAdminSession && adminToken) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);

      if (user) {
        identifyUser(user.uid, { email: user.email || '' });
        identifySentryUser(user.uid, user.email || undefined);
        addBreadcrumb('auth', 'Signed in');
      } else {
        resetUser();
        resetSentryUser();
        addBreadcrumb('auth', 'Signed out');
      }
    });

    return unsubscribe;
  }, [isAdminSession, adminToken]);

  async function login(email: string, password: string) {
    await signInWithEmailAndPassword(auth, email, password);
  }

  async function signup(email: string, password: string) {
    await createUserWithEmailAndPassword(auth, email, password);
  }

  async function logout() {
    if (isAdminSession) {
      adminSession.clearAdminSession();
      setIsAdminSession(false);
      setAdminToken(null);
      window.location.href = '/login';
      return;
    }
    await signOut(auth);
  }

  async function resetPassword(email: string) {
    await sendPasswordResetEmail(auth, email);
  }

  function setAdminSession(accessToken: string, refreshToken: string, email: string) {
    adminSession.storeAdminSession(accessToken, refreshToken, email);
    setIsAdminSession(true);
    setAdminToken(accessToken);
  }

  const value: AuthContextType = {
    user,
    loading,
    isAdminSession,
    adminToken,
    login,
    signup,
    logout,
    resetPassword,
    setAdminSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
